import { useState, useEffect, useCallback } from "react";
import api from "api/api";
import { Link, useNavigate } from "react-router-dom";

//Props Validations
import PropTypes from "prop-types";

// MUI components
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

//import component
import FiltersHeaderLabel from "../components/common/FiltersHeaderLabel";
import Header from "../components/common/Header";
import Loader from "../components/common/Loader";
import MainNavigation from "../components/common/MainNavigation";
import FilterAcrossProductsToggleComponent from "../components/common/FilterAcrossProductsToggleComponent";
import Reset from "../components/common/Reset";
import TTestDialog from "../components/common/TTest/TTestDialog";
import ViewToggle from "../components/common/ViewToggle";
import FullScreenConatiner from "../components/layouts/FullScreenConatiner";
import AnalysisTable from "../components/ict/analysisModule/AnalysisTable";
import AnalysisChart from "../components/ict/chart/AnalysisChart";
import ProductUsage from "components/common/analysisModule/ProductUsage";

// redux
import { useSelector, useDispatch } from "react-redux";
import { loadFragranceMetricsAnalysisResultState, loadDefaultFragranceMetricsAnalysisResultState, loadIsFragranceMetricsAnalysisResultLoadingState, loadSignificanceToggleState, loadDisableSignificanceToggleState } from "store/features/analysisResultsSlice";

import { resetTTestStore } from "store/features/ttestSlice";
import { loadFragranceMetricsRemovedKpiListState } from "store/features/analysisKpiSlice";
import { showNotification } from "store/features/notificationSlice";

// utils
import GetFragranceMetricsResult from "../utils/apis/GetFragranceMetricsResult";
import SignificanceToggleComponent from "components/common/SignificanceToggleComponent";

function FragranceMetricsAnalysis({ headerTitle = "" }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const isLoading = useSelector((state) => state.analysisResultsStore.isFragranceMetricsAnalysisResultLoading);
    const productsSecondaryDataState = useSelector((state) => state.analysisResultsStore.fragranceMetricsAnalysisResult);

    const productsOrder = useSelector((state) => state.productsInfoStore.productsOrder);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    // Analysis type
    const analysisTypeValue = useSelector((state) => state.analysisTypeSelectionStore.fragranceMetricsAnalysisTypeValue);

    const [isTableShown, setIsTableShown] = useState(true);
    const [dataMappingData, setDataMappingData] = useState([]);
    const [isDataMappingDataLoading, setIsDataMappingDataLoading] = useState(false);

    // t-test details
    const [tTestResult, setTTestResult] = useState([]);
    const [testLabel, setTestLabel] = useState("");
    const [isFisher, setIsFisher] = useState(false);
    const [legends, setLegends] = useState([]);

    const [activeTab, setActiveTab] = useState("");

    const btnArr = ["preferenceAnalysis", "fragranceAssociation", "agreementkpi", "productPreferenceAnalysis"];

    const [btnDisabled, setBtnDisabled] = useState({});

    useEffect(() => {
        let _selectedStudies = selectedStudy;

        let disableBtn = _selectedStudies.length === 0;
        let tempObj = {
            fragranceAssociation: disableBtn,
            agreementkpi: disableBtn,
            preferenceAnalysis: disableBtn,
            productPreferenceAnalysis: disableBtn,
        };
        setBtnDisabled(tempObj);
    }, [selectedStudy]);

    const clearResult = useCallback(() => {
        setTTestResult([]);
        setTestLabel("");
        dispatch(resetTTestStore());
        dispatch(loadSignificanceToggleState(false));
        dispatch(loadDisableSignificanceToggleState(true));
    }, [dispatch]);

    const clearTtest = useCallback(
        (product, type) => {
            // clear t test
            if (type === "consumerFilterAll") {
                clearResult();
            } else if (type === "consumerFilter") {
                const tTestPro = tTestResult?.[0]?.colors?.map((datum) => datum.productCode) || [];
                const cfPc = product?.split("_")?.join(" ") || "";
                if (tTestPro.indexOf(cfPc) !== -1) {
                    clearResult();
                }
            }
        },
        [clearResult, tTestResult]
    );

    const getFragranceMetricsResult = useCallback(
        async ({ product = null, coreDataId = null, type = null, consumerFilter = null, pcs = [], selectedStudy = [], allAnalysisData = {}, fetchOnlyCurrent = false }) => {
            clearTtest(product, type);

            dispatch(loadIsFragranceMetricsAnalysisResultLoadingState(true));

            const fragranceMetricsResult = await GetFragranceMetricsResult({
                product,
                coreDataId,
                type,
                consumerFilter,
                pcs,
                selectedStudy,
                productsSecondaryDataState: allAnalysisData?.fragranceMetricsAnalysisResult || [],
                productsOrder,
                productCategoryId,
            });
            if (fragranceMetricsResult) {
                await dispatch(loadFragranceMetricsAnalysisResultState([...fragranceMetricsResult]));
                if (!type) {
                    dispatch(loadDefaultFragranceMetricsAnalysisResultState([...fragranceMetricsResult]));
                }
            }
            dispatch(loadIsFragranceMetricsAnalysisResultLoadingState(false));
            console.log("fetchOnlyCurrent", fetchOnlyCurrent);
        },
        [dispatch, productCategoryId, productsOrder, clearTtest]
    );

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    useEffect(() => {
        async function fetchDataMappingData() {
            try {
                const selectedStudyTypeIds = [...new Set(selectedStudy.map((datum) => datum.study_type))].join(",");
                const selectedStudyTypeMethodIds = [...new Set(selectedStudy.map((datum) => datum.study_type_method))].join(",");
                let url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/all/?archive=false&uniq=true&variable_category=${encodeURIComponent("Fragrance Metrics Analysis")}`;
                if (productCategoryId) {
                    url += `&product_category=${productCategoryId}&study_type=${selectedStudyTypeIds}&study_type_method=${selectedStudyTypeMethodIds}`;
                }
                setIsDataMappingDataLoading(true);
                await api
                    .get(url)
                    .then(async (res) => {
                        setDataMappingData(res.data);
                        setIsDataMappingDataLoading(false);
                    })
                    .catch((error) => {
                        handleError("error", error);
                        setIsDataMappingDataLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsDataMappingDataLoading(false);
            }
        }
        const fetchAnalysisResult = async () => {
            if (!productsSecondaryDataState.length) {
                await getFragranceMetricsResult({
                    product: null,
                    coreDataId: null,
                    type: null,
                    consumerFilter: null,
                    pcs: [],
                    selectedStudy,
                    allAnalysisData: [],
                    fetchOnlyCurrent: true,
                });
            }
        };

        if (selectedStudy.length === 0) {
            navigate("/studies");
        } else {
            fetchAnalysisResult();
            fetchDataMappingData();
        }
    }, [selectedStudy, productsSecondaryDataState, productCategoryId, getFragranceMetricsResult, handleError, navigate]);

    // Clear the removed KPI data from the Redux store on page change to reset the default view.
    // Remove the code below if you want to retain the removed variables.
    useEffect(() => {
        return () => {
            dispatch(loadFragranceMetricsRemovedKpiListState([]));
        };
    }, [dispatch]);

    useEffect(() => {
        clearResult();
    }, []);

    return (
        <>
            {(isLoading || isDataMappingDataLoading) && <Loader />}
            <Header
                title={headerTitle}
                isStudySelected={true}
            />
            <div
                className="ms-4 me-4 mb-0 mt-3 background-color-white box-shadow--2 d-flex"
                style={{
                    borderRadius: "5px",
                }}
            >
                <FiltersHeaderLabel multipleLabel={true} />
            </div>
            <FullScreenConatiner>
                <div
                    className=""
                    style={{
                        width: 150,
                        display: "flex",
                        alignItems: "center",
                        position: "absolute",
                        zIndex: 1,
                    }}
                >
                    <Link to="/studies">
                        <div
                            className="p-2 liner-light-blue-background color-white"
                            style={{
                                borderRadius: "50%",
                                position: "relative",
                                top: -15,
                                left: -15,
                                cursor: "pointer",
                            }}
                        >
                            <span>
                                <ArrowBackIcon className="color-white" />
                            </span>
                        </div>
                    </Link>
                </div>

                <div
                    className="card-btns"
                    style={{ padding: "20px 20px 20px 20px" }}
                >
                    <div
                        style={{
                            display: "flex",
                            zIndex: 9,
                        }}
                    >
                        <div
                            style={{
                                marginBottom: "5px",
                                marginRight: "5px",
                            }}
                        >
                            <ViewToggle
                                value={isTableShown}
                                setValue={setIsTableShown}
                                labels={["Table", "Chart"]}
                            />
                        </div>
                    </div>

                    <div>
                        <SignificanceToggleComponent disabled={legends.length > 2} />
                        <FilterAcrossProductsToggleComponent />
                        <TTestDialog
                            variableCategory="Fragrance Metrics Analysis"
                            functionsSettings={{
                                setIsFisher: setIsFisher,
                                setLegends: setLegends,
                                setTTestResult: setTTestResult,
                                setTestLabel: setTestLabel,
                                clearResult: clearResult,
                            }}
                            analysisLevelData={{
                                moduleName: "fragranceMetrics",
                                analysisTypeValue: analysisTypeValue,
                                boxAnalysisTypeSelection: [],
                                boxAnalysisSelection: {},
                                mappings: dataMappingData.columns,
                            }}
                            tabData={{
                                productsSecondaryDataState: productsSecondaryDataState,
                            }}
                        />
                        <Reset clearTTestResult={clearResult} />
                    </div>
                </div>
                {productsSecondaryDataState.length !== 0 && dataMappingData.length !== 0 && (
                    <>
                        <div
                            style={{
                                minHeight: "calc(100vh - 300px)",
                                padding: "20px",
                            }}
                        >
                            {isTableShown ? (
                                <>
                                    <AnalysisTable
                                        productsSecondaryData={productsSecondaryDataState}
                                        handleFilterSubmit={getFragranceMetricsResult}
                                        selectedStudy={selectedStudy}
                                        analysisLevelData={{
                                            tableConfig: {
                                                stickyColumns: ["Attribute Area", "Attribute"],
                                            },
                                            moduleName: "fragranceMetrics",
                                            analysisTypeValue: analysisTypeValue,
                                            mappings: dataMappingData.columns,
                                        }}
                                        tTest={{
                                            tTestLegends: legends,
                                            isFisher: isFisher,
                                            tTestResult: tTestResult,
                                            testLabel: testLabel,
                                        }}
                                    />
                                </>
                            ) : (
                                <AnalysisChart
                                    productsSecondaryData={productsSecondaryDataState}
                                    analysisLevelData={{
                                        moduleName: "fragranceMetrics",
                                        analysisTypeValue: analysisTypeValue,
                                        boxAnalysisTypeSelection: [],
                                        boxAnalysisSelection: {},
                                        mappings: dataMappingData.columns,
                                    }}
                                    settings={{
                                        activeTab: activeTab,
                                        setActiveTab: setActiveTab,
                                    }}
                                    tTest={{
                                        tTestLegends: legends,
                                        isFisher: isFisher,
                                        tTestResult: tTestResult,
                                        testLabel: testLabel,
                                    }}
                                />
                            )}
                        </div>
                    </>
                )}
                {selectedStudy.length == 1 && (
                    <div style={{ display: "flex", justifyContent: "flex-end", width: "100%", gap: "8px" }}>
                        <ProductUsage />
                    </div>
                )}
                <div
                    style={{
                        position: "relative",
                        top: "5px",
                        marginTop: "30px",
                    }}
                >
                    <MainNavigation
                        buttons={btnArr}
                        isFragrance={true}
                        btnDisabledObj={btnDisabled}
                    />
                </div>
            </FullScreenConatiner>
        </>
    );
}

// Typechecking props for the FragranceMetricsAnalysis
FragranceMetricsAnalysis.propTypes = {
    headerTitle: PropTypes.string,
};

export default FragranceMetricsAnalysis;
